import * as React from 'react';
import { Upload, FileText, X, Check, ArrowLeft, Settings, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createConverter, isConversionSuccessful } from '../services/enhanced-file-converter';
import { FileConversionResult } from '../types/medical';
// ✨ NUEVO: Usar tipos unificados del sistema
import type { ExtractedFileData, AutoFillContext, ExtendedFileConversionResult } from '../types/enhancedSystem';
// 🚀 NUEVO: Importar el parser robusto
import { RobustParserIntegration, IntegrationResult } from '../services/robustParserIntegration';
import { RobustEMGParser } from '../services/robustEMGParser';
import { extractPatientFromFilename } from '../services/xltekTemplateParser';
import { isValidPatientId, parseFullName } from '../utils/patientNameParser';
import {
  inferGenieAnswersFromClinicalSources,
  inferSymptomsFromClinicalSources,
} from '../services/clinicalSymptomsExtractor';
import ExtractionLogPanel from './ExtractionLogPanel';
import {
  buildExtractionLog,
  buildFallbackExtractionLog,
  buildSimpleExtractionLog,
  type ExtractionLogSummary,
} from '../utils/extractionLogBuilder';

export interface FileUploadCompletePayload {
  extractedData?: ExtractedFileData;
  sourceFile?: string;
  autoFillMode: boolean;
  fallbackMode?: boolean;
  fileResult?: ExtendedFileConversionResult;
}

interface FileUploadProps {
  embedded?: boolean;
  onComplete?: (payload: FileUploadCompletePayload) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ embedded = false, onComplete }) => {
  const [file, setFile] = React.useState<File | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [uploadStatus, setUploadStatus] = React.useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [conversionResult, setConversionResult] = React.useState<FileConversionResult | null>(null);
  // 🚀 NUEVO: Estado para el parser robusto
  const [robustParserResult, setRobustParserResult] = React.useState<IntegrationResult | null>(null);
  const [showParserOptions, setShowParserOptions] = React.useState(false);
  const [useRobustParser, setUseRobustParser] = React.useState(true);
  // Panel de revisión: los datos extraídos deben confirmarse antes de pasar al cuestionario.
  const [pendingResult, setPendingResult] = React.useState<{ mockResult: ExtendedFileConversionResult; extractedData: any } | null>(null);
  const [allowFallbackContinue, setAllowFallbackContinue] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const allowedTypes = [
    'application/msword',                                                    // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/rtf',                                                      // .rtf
    'application/pdf'                                                       // .pdf
  ];

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const allowedExtensions = ['doc', 'docx', 'rtf', 'pdf'];

  const validateFile = (file: File): boolean => {
    // En Windows, .rtf/.doc suelen llegar con file.type vacío o genérico,
    // por lo que validamos también por extensión para no rechazar archivos válidos.
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const hasAllowedMime = !!file.type && allowedTypes.includes(file.type);
    const hasAllowedExtension = allowedExtensions.includes(extension);

    if (!hasAllowedMime && !hasAllowedExtension) {
      setErrorMessage('Formato de archivo no soportado. Por favor, sube un archivo Word (.doc/.docx), RTF o PDF.');
      return false;
    }
    if (file.size === 0) {
      setErrorMessage('El archivo está vacío. Selecciona un archivo válido.');
      return false;
    }
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setErrorMessage('El archivo es demasiado grande. El tamaño máximo permitido es 10MB.');
      return false;
    }
    return true;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && validateFile(droppedFile)) {
      setFile(droppedFile);
      setErrorMessage('');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && validateFile(selectedFile)) {
      setFile(selectedFile);
      setErrorMessage('');
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploadStatus('uploading');
    
    try {
      let extractedData;
      
      if (useRobustParser) {
        // 🚀 Priorizar extractor estructurado: DOCX/PDF/RTF no deben depender del parser legacy.
        console.log('📁 Procesando archivo con sistema estructurado:', file.name);
        extractedData = await processFileWithAdvancedSystem(file);

        if (!isExtractedDataUsable(extractedData)) {
          console.log('⚠️ Sistema estructurado sin datos clínicos suficientes, probando parser robusto como fallback');
          const robustResult = await processFileWithRobustParser(file);
          setRobustParserResult(robustResult);

          if (robustResult.success && robustResult.data && isRobustResultUsable(robustResult.data)) {
            extractedData = convertRobustResultToExtractedData(robustResult.data);
          }
        }
      } else {
        // 🚀 USAR EL PROCESADOR AVANZADO PARA EXTRAER DATOS REALES
        console.log('📁 Procesando archivo con sistema avanzado:', file.name);
        
        // Crear procesador avanzado (simulado por compatibilidad)
        extractedData = await processFileWithAdvancedSystem(file);
      }
      
      // Crear resultado con datos extraídos
      const totalExtractedFields =
        (extractedData.ncsResults?.filter((ncs: any) =>
          (ncs.latency || 0) > 0 ||
          (ncs.peakLatency || 0) > 0 ||
          (ncs.amplitude || 0) > 0 ||
          (ncs.velocity || 0) > 0 ||
          ncs.absentResponse ||
          ncs.qualitativeFindings?.length
        ).length || 0) +
        (extractedData.emgResults?.length || 0) +
        (extractedData.specialStudies?.length || 0);

      const mockResult: ExtendedFileConversionResult = {
        success: true,
        report: {
          patient: {
            id: isValidPatientId(extractedData.patient?.id || '') ? extractedData.patient.id : generatePatientId(),
            age: extractedData.patient?.age || 0,
            gender: (extractedData.patient?.sex === 'female' ? 'F' : 'M') as 'M' | 'F',
            height: 170, // Valores por defecto
            weight: 70,
            bmi: 24.2
          },
          tests: [], // Los tests NCS se mapearán luego
          diagnosis: 'Diagnóstico pendiente de evaluación',
          notes: 'Archivo procesado exitosamente con datos extraídos',
          metadata: {
            originalFormat: file.type,
            conversionDate: new Date().toISOString(),
            confidenceScore: extractedData.quality?.confidence || 0.8,
            processingErrors: [],
            fieldsMapped: totalExtractedFields
          }
        },
        errors: [],
        warnings: extractedData.warnings || [],
        processingTime: extractedData.quality?.processingTime || 1500,
        // Agregar datos extraídos como propiedad adicional para compatibilidad
        extractedData: extractedData
      };
      
      setConversionResult(mockResult);
      // Dejar los datos en revisión en lugar de redirigir automáticamente:
      // el usuario confirma antes de autollenar el cuestionario (seguridad clínica).
      setPendingResult({ mockResult, extractedData });
      setUploadStatus('success');
      
      console.log('✅ Archivo procesado exitosamente');
      console.log('📊 Datos extraídos:', extractedData);
      
    } catch (error) {
      setUploadStatus('error');
      setErrorMessage('Error al procesar el archivo. Puedes continuar con datos básicos y completarlos manualmente.');
      setAllowFallbackContinue(true);
      console.error('Error processing file:', error);
    }
  };

  const finishUpload = (payload: FileUploadCompletePayload) => {
    if (onComplete) {
      onComplete(payload);
      return;
    }
    navigate('/clinical', { state: payload });
  };

  // Confirmar y continuar el flujo clínico con los datos extraídos.
  const proceedToQuestionnaire = () => {
    if (!pendingResult || !file) return;
    finishUpload({
      fileResult: pendingResult.mockResult,
      extractedData: pendingResult.extractedData,
      autoFillMode: true,
      sourceFile: file.name
    });
  };

  // Continuar manualmente cuando el procesamiento falló.
  const proceedWithFallback = () => {
    finishUpload({
      autoFillMode: false,
      sourceFile: file?.name,
      fallbackMode: true
    });
  };

  const resetUpload = () => {
    setFile(null);
    setPendingResult(null);
    setConversionResult(null);
    setRobustParserResult(null);
    setAllowFallbackContinue(false);
    setUploadStatus('idle');
    setErrorMessage('');
  };

  // 🚀 NUEVA FUNCIÓN: PROCESAR CON PARSER ROBUSTO
  const processFileWithRobustParser = async (file: File): Promise<IntegrationResult> => {
    try {
      console.log('🔗 Iniciando procesamiento con parser robusto...');
      
      // Leer contenido preservando tablas para DOCX/PDF/RTF antes del parser robusto.
      const fileText = await readFileForParsing(file);
      
      // Crear integración con configuración optimizada
      const integration = RobustParserIntegration.getInstance({
        useRobustParser: true,
        fallbackToOriginal: true,
        enableComparison: true,
        enableValidation: true,
        parserConfig: {
          enableAdaptiveLearning: true,
          enableFuzzyMatching: true,
          enableCrossValidation: true,
          strictMode: false,
          debugMode: false,
          minConfidenceThreshold: 0.6
        }
      });
      
      // Procesar con el parser robusto
      const result = await integration.processEMGReport(fileText, file.name);
      
      console.log('✅ Parser robusto completado:', {
        success: result.success,
        confidence: result.data?.confidence,
        motorNCS: result.data?.motorNCS.length,
        sensoryNCS: result.data?.sensoryNCS.length,
        needleEMG: result.data?.needleEMG.length
      });
      
      return result;
      
    } catch (error) {
      console.error('❌ Error en parser robusto:', error);
      return {
        success: false,
        data: null,
        recommendations: [],
        errors: [`Error en parser robusto: ${error instanceof Error ? error.message : 'Error desconocido'}`],
        warnings: []
      };
    }
  };

  const isRobustResultUsable = (robustData: any): boolean => {
    const totalClinicalRows =
      (robustData.motorNCS?.length || 0) +
      (robustData.sensoryNCS?.length || 0) +
      (robustData.needleEMG?.length || 0) +
      (robustData.specialStudies?.length || 0);

    return totalClinicalRows > 0;
  };

  const isExtractedDataUsable = (data: any): boolean => {
    return (
      (data?.ncsResults?.length || 0) +
      (data?.emgResults?.length || 0) +
      (data?.specialStudies?.length || 0)
    ) > 0;
  };

  // 🔄 NUEVA FUNCIÓN: CONVERTIR RESULTADO ROBUSTO A FORMATO EXISTENTE
  const convertRobustResultToExtractedData = (robustData: any) => {
    const ncsCount = (robustData.motorNCS?.length || 0) + (robustData.sensoryNCS?.length || 0);
    const emgCount = robustData.needleEMG?.length || 0;
    return {
      patient: buildExtractedPatient(file?.name || '', robustData.patient),
      ncsResults: [
        ...robustData.motorNCS.map((ncs: any) => ({
          ...ncs,
          type: 'motor'
        })),
        ...robustData.sensoryNCS.map((ncs: any) => ({
          ...ncs,
          type: 'sensory'
        }))
      ],
      emgResults: robustData.needleEMG || [],
      specialStudies: robustData.specialStudies || [],
      symptoms: inferSymptomsFromRobustData(robustData),
      warnings: robustData.warnings || [],
      quality: {
        confidence: robustData.confidence || 0,
        sectionsFound: ncsCount + emgCount + (robustData.specialStudies?.length || 0),
        processingTime: 0,
        textLength: 0,
        recommendations: robustData.recommendations || [],
        extractionLog: buildSimpleExtractionLog(file!, {
          source: 'Parser robusto (fallback)',
          confidence: robustData.confidence || 0,
          warnings: robustData.warnings || [],
          infoMessages: [
            `Registros extraídos: ${ncsCount} NCS · ${emgCount} EMG · ${robustData.specialStudies?.length || 0} estudios especiales`,
          ],
        }),
      }
    };
  };

  // 🧠 FUNCIÓN AUXILIAR: LEER ARCHIVO COMO TEXTO
  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  const readFileForParsing = async (file: File): Promise<string> => {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (extension === 'txt' || extension === 'csv') {
      return readFileAsText(file);
    }

    const { StructuredDocumentExtractor } = await import('../services/structuredDocumentExtractor');
    const extractedDocument = await StructuredDocumentExtractor.extract(file);
    return extractedDocument.markdown || extractedDocument.plainText;
  };

  const extractNarrativeNCSFallback = async (file: File) => {
    try {
      const { StructuredDocumentExtractor } = await import('../services/structuredDocumentExtractor');
      const { ClinicalNarrativeExtractor } = await import('../services/clinicalNarrativeExtractor');
      const extractedDocument = await StructuredDocumentExtractor.extract(file);
      const narrativeResult = ClinicalNarrativeExtractor.extract(extractedDocument);

      return narrativeResult.ncsFindings.flatMap((finding) => {
        const sides = finding.side === 'bilateral' ? ['left', 'right'] as const : [finding.side === 'right' ? 'right' as const : 'left' as const];
        return sides.map((side) => ({
        nerve: finding.nerve,
        side,
        type: finding.type === 'motor' ? 'motor' as const : 'sensory' as const,
        latency: finding.latency?.value || finding.peakLatency?.value || 0,
        peakLatency: finding.peakLatency?.value,
        amplitude: finding.absentResponse ? 0 : finding.amplitude?.value || 0,
        velocity: finding.velocity?.value || 0,
        status: finding.status,
        findings: finding.qualitativeFindings,
        qualitativeFindings: finding.qualitativeFindings,
        absentResponse: finding.absentResponse,
        requiresReview: true,
        fieldConfidence: {
          latency: finding.latency?.confidence || finding.peakLatency?.confidence,
          amplitude: finding.amplitude?.confidence,
          velocity: finding.velocity?.confidence
        },
        extractionSource: {
          parser: finding.parser,
          confidence: finding.confidence,
          evidence: finding.evidence
        }
        }));
      });
    } catch (error) {
      console.warn('⚠️ Fallback narrativo NCS no disponible:', error);
      return [];
    }
  };

  // 🧠 FUNCIÓN AUXILIAR: INFERIR SÍNTOMAS DE DATOS ROBUSTOS
  const inferSymptomsFromRobustData = (data: any) => {
    const symptoms: any = {};
    
    // Analizar NCS motor
    if (data.motorNCS?.length > 0) {
      const abnormalMotor = data.motorNCS.filter((ncs: any) => ncs.status === 'abnormal');
      if (abnormalMotor.length > 0) {
        symptoms.weakness = { present: true, severity: 'moderate' };
      }
    }
    
    // Analizar NCS sensitivo
    if (data.sensoryNCS?.length > 0) {
      const abnormalSensory = data.sensoryNCS.filter((ncs: any) => ncs.status === 'abnormal');
      if (abnormalSensory.length > 0) {
        symptoms.paresthesias = { present: true, severity: 'mild' };
      }
    }
    
    // Analizar EMG de aguja
    if (data.needleEMG?.length > 0) {
      const abnormalEMG = data.needleEMG.filter((emg: any) => 
        emg.spontaneousActivity?.fibrillations || emg.spontaneousActivity?.positiveWaves
      );
      if (abnormalEMG.length > 0) {
        symptoms.weakness = { present: true, severity: 'severe' };
        symptoms.pain = { present: true };
      }
    }
    
    return symptoms;
  };

  // 🧠 FUNCIÓN PARA PROCESAR EL ARCHIVO CON EL SISTEMA AVANZADO
  const processFileWithAdvancedSystem = async (file: File) => {
          // 🔥 USAR EL NUEVO SISTEMA INTEGRADO - EnhancedFileConverter
      console.log('🚀 Procesando archivo con EnhancedFileConverter...');
      
      try {
        // Importar el nuevo procesador integrado
        const { EnhancedFileConverter } = await import('../services/enhanced-file-converter');
        
        // Crear procesador con configuración de producción
        const processor = EnhancedFileConverter.getInstance({
          enableOCR: false,
          enableAIEnhancement: false,
          enableValidation: true,
          minConfidenceThreshold: 0.6,
          debugMode: false
        });
      
              // Procesar archivo con sistema completo
        const processingResult = await processor.convert(file);
      
      // Si el procesador reporta errores críticos, detén el flujo; de lo contrario, continúa aun con baja confianza.
      if (!processingResult.success && (processingResult.errors?.length ?? 0) > 0) {
        console.error('❌ Error en procesamiento crítico:', processingResult.errors);
        throw new Error(`Error en procesamiento: ${formatProcessingErrors(processingResult.errors)}`);
      }
      
      const ncsResults = processingResult.data?.ncsResults || [];
      const emgResults = processingResult.data?.emgResults || [];
      const fallbackNarrativeNCS = ncsResults.length === 0 && emgResults.length === 0
        ? await extractNarrativeNCSFallback(file)
        : [];
      const usedNarrativeFallback = ncsResults.length === 0 && fallbackNarrativeNCS.length > 0;

      const extractionLog = buildExtractionLog(processingResult, file, {
        fallbackNarrative: usedNarrativeFallback,
      });

      // Los datos ya vienen en formato optimizado
      const visibleWarnings = [
        ...(processingResult.warnings || []),
        ...(usedNarrativeFallback ? ['Se usó extracción narrativa como fallback porque no se detectaron tablas NCS/EMG.'] : [])
      ].filter(warning => !isTechnicalWarning(warning));

      const clinicalNotes = [
        processingResult.data?.notes,
        processingResult.data?.diagnosis,
        processingResult.data?.conclusion,
      ].filter(Boolean).join(' ').trim();

      const inferredSymptoms = inferSymptomsFromClinicalSources({
        notes: processingResult.data?.notes,
        diagnosis: processingResult.data?.diagnosis,
        conclusion: processingResult.data?.conclusion,
      });

      const genieClinicalAnswers = inferGenieAnswersFromClinicalSources({
        notes: processingResult.data?.notes,
        diagnosis: processingResult.data?.diagnosis,
        conclusion: processingResult.data?.conclusion,
      });

      const extractedData = {
        patient: buildExtractedPatient(file.name, processingResult.data?.patient as any),
        ncsResults: ncsResults.length > 0 ? ncsResults : fallbackNarrativeNCS,
        emgResults,
        specialStudies: processingResult.data?.specialStudies || [],
        symptoms: inferredSymptoms || processingResult.data?.clinicalSymptoms || {},
        clinicalNotes: clinicalNotes || undefined,
        genieClinicalAnswers: Object.keys(genieClinicalAnswers).length > 0 ? genieClinicalAnswers : undefined,
        warnings: visibleWarnings,
        // Metadatos de calidad mejorados
        quality: {
          confidence: processingResult.confidence || 0,
          sectionsFound: (ncsResults.length > 0 ? 1 : 0) +
            (emgResults.length > 0 ? 1 : 0) +
            ((processingResult.data?.specialStudies?.length || 0) > 0 ? 1 : 0),
          processingTime: processingResult.metadata?.processingTimeMs || 0,
          textLength: processingResult.metadata?.extractedTextLength || 0,
          fieldsMapped: (ncsResults.filter((ncs: any) =>
            (ncs.latency || 0) > 0 ||
            (ncs.peakLatency || 0) > 0 ||
            (ncs.amplitude || 0) > 0 ||
            (ncs.velocity || 0) > 0 ||
            ncs.absentResponse ||
            ncs.qualitativeFindings?.length
          ).length) + emgResults.length + (processingResult.data?.specialStudies?.length || 0),
          recommendations: processingResult.data?.recommendations || [],
          extractionSummary: processingResult.metadata?.extractionSummary,
          extractionLog,
        }
      };

      console.log('✅ Datos extraídos con EnhancedFileConverter:', extractedData);
      console.log('📊 Estadísticas de calidad:', {
        confidence: `${(processingResult.confidence * 100).toFixed(1)}%`,
        sectionsProcessed: processingResult.metadata?.sectionsFound?.length || 0,
          dataPoints: (extractedData.ncsResults?.length || 0) + (extractedData.emgResults?.length || 0),
        processingTime: `${processingResult.metadata?.processingTimeMs}ms`,
        recommendations: processingResult.data?.recommendations?.length || 0
      });
      
      return extractedData;
      
    } catch (error) {
      console.error('❌ Error en EnhancedFileProcessor:', error);
      const fallbackNarrativeNCS = await extractNarrativeNCSFallback(file);
      const errorMessage = error instanceof Error ? error.message : formatProcessingErrors(error);
      const fallbackWarnings = [
        'Error en procesamiento automático con EnhancedFileProcessor',
        fallbackNarrativeNCS.length > 0
          ? 'Se recuperaron hallazgos NCS mediante extracción narrativa de respaldo'
          : 'Se utilizaron datos mínimos como fallback',
        `Error técnico: ${errorMessage}`
      ];
      
      // Fallback mejorado: todavía intentar extracción estructurada+narrativa antes de usar datos mínimos.
      const fallbackData = {
        patient: buildExtractedPatient(file.name),
        ncsResults: fallbackNarrativeNCS,
        emgResults: [],
        specialStudies: [],
        symptoms: {},
        warnings: fallbackWarnings.filter(warning => !isTechnicalWarning(warning)),
        quality: {
          confidence: fallbackNarrativeNCS.length > 0 ? 0.55 : 0,
          sectionsFound: fallbackNarrativeNCS.length,
          processingTime: 0,
          textLength: 0,
          recommendations: ['Revisar formato del archivo', 'Verificar contenido médico'],
          extractionLog: buildFallbackExtractionLog(file, errorMessage, fallbackWarnings),
        }
      };
      
      console.log('🔄 Usando datos fallback mejorados:', fallbackData);
      return fallbackData;
    }
  };

  const formatProcessingErrors = (errors: any): string => {
    if (!errors) return 'Error desconocido';
    if (Array.isArray(errors)) {
      return errors.map(formatProcessingErrors).join(', ');
    }
    if (errors instanceof Error) return errors.message;
    if (typeof errors === 'string') return errors;
    if (typeof errors === 'object') {
      return errors.message || errors.code || JSON.stringify(errors);
    }
    return String(errors);
  };

  const isTechnicalWarning = (warning: string): boolean => {
    return /w:tblPrEx|unrecognised element|unrecognized element|mammoth/i.test(String(warning || ''));
  };

  // 🧠 FUNCIÓN AUXILIAR PARA INFERIR SÍNTOMAS
  const inferSymptomsFromData = (data: any) => {
    const symptoms: any = {};
    
    if (data?.ncsResults?.length > 0) {
      const hasAbnormalNCS = data.ncsResults.some((ncs: any) => ncs.status === 'abnormal');
      if (hasAbnormalNCS) {
        symptoms.paresthesias = { present: true, severity: 'mild' };
        symptoms.weakness = { present: true, severity: 'mild' };
      }
    }
    
    if (data?.emgResults?.length > 0) {
      const hasAbnormalEMG = data.emgResults.some((emg: any) => 
        emg.spontaneousActivity?.fibrillations || emg.spontaneousActivity?.positiveWaves
      );
      if (hasAbnormalEMG) {
        symptoms.weakness = { present: true, severity: 'moderate' };
        symptoms.pain = { present: true };
      }
    }
    
    return symptoms;
  };

  // 🧠 FUNCIÓN AUXILIAR PARA CALCULAR EDAD
  const calculateAge = (dateOfBirth: string): number => {
    try {
      const birthDate = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      return age;
    } catch (error) {
      return 0;
    }
  };

  // Funciones auxiliares
  const resolvePatientDisplayName = (
    fileName: string,
    patient?: { firstName?: string; lastName?: string; name?: string }
  ): string => {
    if (patient?.name?.trim()) return patient.name.trim();
    const fromParts = [patient?.firstName, patient?.lastName].filter(Boolean).join(' ').trim();
    if (fromParts) return fromParts;
    const fromFilename = extractPatientFromFilename(fileName).name;
    if (fromFilename) return fromFilename;
    return extractPatientName(fileName);
  };

  const buildExtractedPatient = (
    fileName: string,
    patient?: {
      firstName?: string;
      lastName?: string;
      name?: string;
      id?: string;
      age?: number | null;
      sex?: 'male' | 'female' | 'other';
    }
  ) => {
    const displayName = resolvePatientDisplayName(fileName, patient);
    const filenamePatient = extractPatientFromFilename(fileName);
    const parsed = parseFullName(displayName);
    const id = isValidPatientId(patient?.id || '') ? patient!.id! : '';

    return {
      name: displayName,
      firstName: patient?.firstName || parsed.firstName,
      lastName: patient?.lastName || parsed.lastName,
      id: id || undefined,
      age: patient?.age ?? null,
      sex: patient?.sex,
      filenameName: filenamePatient.name,
    };
  };

  const extractPatientName = (filename: string): string => {
    const cleanName = filename.replace(/\.[^/.]+$/, "").replace(/[_-]/g, ' ');
    return cleanName || 'Paciente EMG';
  };

  const generatePatientId = (): string => {
    return 'EMG-' + Date.now().toString().slice(-6);
  };

  const getFileIcon = () => {
    if (file?.type.includes('pdf')) return 'PDF';
    if (file?.type.includes('word')) return 'WORD';
    if (file?.type.includes('rtf')) return 'RTF';
    return 'DOC';
  };

  const uploadContent = (
    <>
          {!embedded && (
            <h1 className="text-2xl font-bold mb-6 text-center">Subir Archivo de Evaluación</h1>
          )}
          {embedded && (
            <p className="text-gray-300 text-center mb-6">
              Sube un archivo con datos electrodiagnósticos para comenzar (Word, RTF o PDF)
            </p>
          )}
          
          {/* 🚀 NUEVO: Opciones del Parser Robusto */}
          <div className="mb-6 p-4 bg-gray-700 rounded-lg border border-gray-600">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-green-400 flex items-center">
                <Settings className="h-5 w-5 mr-2" />
                Opciones de Procesamiento
              </h2>
              <button
                onClick={() => setShowParserOptions(!showParserOptions)}
                className="text-blue-400 hover:text-blue-300 transition-colors"
              >
                {showParserOptions ? 'Ocultar' : 'Mostrar'} Opciones
              </button>
            </div>
            
            {showParserOptions && (
              <div className="space-y-3">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={useRobustParser}
                    onChange={(e) => setUseRobustParser(e.target.checked)}
                    className="rounded"
                  />
                  <div>
                    <span className="font-medium">Usar Parser Robusto</span>
                    <p className="text-sm text-gray-400">
                      Sistema avanzado con validación fisiológica y aprendizaje adaptativo
                    </p>
                  </div>
                </label>
                
                {useRobustParser && (
                  <div className="ml-6 p-3 bg-gray-800 rounded border border-gray-600">
                    <p className="text-sm text-gray-300 mb-2">
                      <strong>Ventajas del Parser Robusto:</strong>
                    </p>
                    <ul className="text-xs text-gray-400 space-y-1">
                      <li>• Validación automática de rangos fisiológicos</li>
                      <li>• Soporte para 8+ nervios diferentes</li>
                      <li>• Aprendizaje adaptativo que mejora con cada uso</li>
                      <li>• Fallback automático al sistema original</li>
                      <li>• Comparación automática de resultados</li>
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? 'border-blue-500 bg-blue-500/10'
                : 'border-gray-600 hover:border-gray-500'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center space-x-3">
                  <FileText className="h-8 w-8 text-blue-400" />
                  <span className="text-lg font-medium">{file.name}</span>
                  <button
                    onClick={() => setFile(null)}
                    className="p-1 hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-400 hover:text-white" />
                  </button>
                </div>
                <div className="text-sm text-gray-400">
                  {(file.size / 1024 / 1024).toFixed(2)} MB • {getFileIcon()}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <Upload className="h-12 w-12 text-gray-400" />
                </div>
                <div>
                  <p className="text-lg">
                    Arrastra y suelta tu archivo aquí o{' '}
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      selecciona un archivo
                    </button>
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    Formatos soportados: Word, RTF, PDF • Tamaño máximo: 10MB
                  </p>
                </div>
              </div>
            )}
          </div>

          {errorMessage && (
            <div className="mt-4 p-4 bg-red-900/50 border border-red-500/50 rounded-lg text-red-200">
              {errorMessage}
              {allowFallbackContinue && (
                <div className="mt-3">
                  <button
                    onClick={proceedWithFallback}
                    className="px-4 py-2 rounded-lg bg-yellow-600 hover:bg-yellow-500 text-white text-sm font-medium"
                  >
                    Continuar con datos básicos
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 🔍 NUEVO: Panel de revisión previo al autollenado */}
          {uploadStatus === 'success' && pendingResult && (() => {
            const data = pendingResult.extractedData || {};
            const confidence = Math.round((data.quality?.confidence || 0) * 100);
            const ncsCount = data.ncsResults?.length || 0;
            const emgCount = data.emgResults?.length || 0;
            const specialCount = data.specialStudies?.length || 0;
            const warnings: string[] = data.warnings || [];
            const lowConfidence = confidence < 60;
            const sexLabel = data.patient?.sex === 'female' ? 'Femenino' : data.patient?.sex === 'male' ? 'Masculino' : 'No detectado';
            const hasValidId = isValidPatientId(data.patient?.id || '');
            const hasAge = data.patient?.age != null && data.patient.age > 0;
            const hasName = !!(data.patient?.name || data.patient?.firstName);
            const demographyScore = [hasName, hasValidId, hasAge, data.patient?.sex].filter(Boolean).length;
            const demographyConfidence = Math.round((demographyScore / 4) * 100);
            const demographyIncomplete = demographyConfidence < 75;
            const parsedName = parseFullName(data.patient?.name || `${data.patient?.firstName || ''} ${data.patient?.lastName || ''}`.trim());
            const extractionLog = data.quality?.extractionLog as ExtractionLogSummary | undefined;

            return (
              <div className="mt-4 p-4 bg-gray-700 rounded-lg border border-gray-600">
                <h3 className="text-lg font-semibold mb-3 text-green-400 flex items-center">
                  <Check className="h-5 w-5 mr-2" />
                  Revisión de datos extraídos
                </h3>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-gray-400">Confianza tablas</div>
                    <div className={`font-bold ${lowConfidence ? 'text-yellow-400' : 'text-green-400'}`}>{confidence}%</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Confianza demografía</div>
                    <div className={`font-bold ${demographyIncomplete ? 'text-yellow-400' : 'text-green-400'}`}>{demographyConfidence}%</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">NCS</div>
                    <div className="font-bold text-purple-400">{ncsCount}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">EMG aguja</div>
                    <div className="font-bold text-yellow-400">{emgCount}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Estudios especiales</div>
                    <div className="font-bold text-blue-400">{specialCount}</div>
                  </div>
                </div>

                <div className="mb-4 p-3 bg-gray-800 rounded border border-gray-600 text-sm text-gray-300">
                  <div><span className="text-gray-400">Paciente:</span> {data.patient?.name || 'No detectado'}</div>
                  <div><span className="text-gray-400">Nombre(s):</span> {parsedName.firstName || '—'}</div>
                  <div><span className="text-gray-400">Apellidos:</span> {parsedName.lastName || '—'}</div>
                  <div><span className="text-gray-400">ID:</span> {hasValidId ? data.patient.id : 'No detectado'}</div>
                  <div><span className="text-gray-400">Edad:</span> {hasAge ? data.patient.age : 'No detectada'}</div>
                  <div><span className="text-gray-400">Sexo:</span> {sexLabel}</div>
                </div>

                {demographyIncomplete && (
                  <div className="mb-4 p-3 bg-yellow-900/40 border border-yellow-500/40 rounded text-yellow-200 text-sm">
                    Datos demográficos incompletos. Verifique nombre, ID, edad y sexo en el cuestionario antes de continuar.
                  </div>
                )}

                {lowConfidence && (
                  <div className="mb-4 p-3 bg-yellow-900/40 border border-yellow-500/40 rounded text-yellow-200 text-sm">
                    Confianza de extracción baja. Revisa cuidadosamente los datos en el cuestionario antes de generar el reporte.
                  </div>
                )}

                {extractionLog && (
                  <div className="mb-4">
                    <ExtractionLogPanel log={extractionLog} />
                  </div>
                )}

                {!extractionLog && warnings.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-md font-semibold mb-2 text-yellow-400">Advertencias</h4>
                    <ul className="text-sm text-gray-300 space-y-1">
                      {warnings.map((warning, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <span className="text-yellow-400">•</span>
                          <span>{warning}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={proceedToQuestionnaire}
                    className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium flex items-center"
                  >
                    <Check className="h-5 w-5 mr-2" />
                    Continuar al cuestionario
                  </button>
                  <button
                    onClick={resetUpload}
                    className="px-5 py-2 rounded-lg bg-gray-600 hover:bg-gray-500 text-white font-medium"
                  >
                    Subir otro archivo
                  </button>
                </div>
              </div>
            );
          })()}

          {/* 🚀 NUEVO: Resultados del Parser Robusto */}
          {robustParserResult && (
            <div className="mt-4 p-4 bg-gray-700 rounded-lg border border-gray-600">
              <h3 className="text-lg font-semibold mb-3 text-green-400 flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Resultados del Parser Robusto
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                <div>
                  <div className="text-sm text-gray-400">Estado</div>
                  <div className={`font-bold ${robustParserResult.success ? 'text-green-400' : 'text-red-400'}`}>
                    {robustParserResult.success ? '✅ Exitoso' : '❌ Fallido'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-400">Confianza</div>
                  <div className="font-bold text-blue-400">
                    {robustParserResult.data?.confidence ? 
                      `${(robustParserResult.data.confidence * 100).toFixed(1)}%` : 
                      'N/A'
                    }
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-400">NCS Motor</div>
                  <div className="font-bold text-purple-400">
                    {robustParserResult.data?.motorNCS?.length || 0}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-400">EMG Aguja</div>
                  <div className="font-bold text-yellow-400">
                    {robustParserResult.data?.needleEMG?.length || 0}
                  </div>
                </div>
              </div>

              {/* Comparación si está disponible */}
              {robustParserResult.comparison && (
                <div className="mb-3 p-3 bg-gray-800 rounded border border-gray-600">
                  <h4 className="text-md font-semibold mb-2 text-blue-400">Comparación de Métodos</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <span className="text-gray-400">Mejora Completitud:</span>
                      <span className={`ml-2 font-bold ${
                        robustParserResult.comparison.dataCompleteness > 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {(robustParserResult.comparison.dataCompleteness * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400">Mejora Confianza:</span>
                      <span className={`ml-2 font-bold ${
                        robustParserResult.comparison.confidenceImprovement > 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {(robustParserResult.comparison.confidenceImprovement * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400">Score General:</span>
                      <span className={`ml-2 font-bold ${
                        robustParserResult.comparison.overallScore > 0.2 ? 'text-green-400' :
                        robustParserResult.comparison.overallScore < -0.2 ? 'text-red-400' : 'text-yellow-400'
                      }`}>
                        {(robustParserResult.comparison.overallScore * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Recomendaciones */}
              {robustParserResult.recommendations.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-md font-semibold mb-2 text-green-400">Recomendaciones</h4>
                  <ul className="text-sm text-gray-300 space-y-1">
                    {robustParserResult.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-green-400">•</span>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Advertencias */}
              {robustParserResult.warnings.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-md font-semibold mb-2 text-yellow-400">Advertencias</h4>
                  <ul className="text-sm text-gray-300 space-y-1">
                    {robustParserResult.warnings.map((warning, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-yellow-400">•</span>
                        <span>{warning}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".doc,.docx,.rtf,.pdf"
            className="hidden"
          />

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleUpload}
              disabled={!file || uploadStatus === 'uploading' || uploadStatus === 'success'}
              className={`
                px-6 py-2 rounded-lg font-medium flex items-center
                ${
                  !file || uploadStatus === 'uploading' || uploadStatus === 'success'
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-500 text-white'
                }
              `}
            >
              {uploadStatus === 'uploading' && (
                <React.Fragment>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                  Procesando...
                </React.Fragment>
              )}
              {uploadStatus === 'success' && (
                <React.Fragment>
                  <Check className="h-5 w-5 mr-2" />
                  ¡Procesado con éxito!
                </React.Fragment>
              )}
              {(uploadStatus === 'idle' || uploadStatus === 'error') && (
                <React.Fragment>
                  <Upload className="h-5 w-5 mr-2" />
                  Procesar archivo
                </React.Fragment>
              )}
            </button>
          </div>
    </>
  );

  if (embedded) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 text-white">
        {uploadContent}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Volver al inicio
        </button>

        <div className="bg-gray-800 rounded-lg p-8 shadow-xl">
          {uploadContent}
        </div>
      </div>
    </div>
  );
};

export default FileUpload;